import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import invariant from 'tiny-invariant';

import { getRutilusGraphqlClient } from '../../../src/api/graphql/getRutilusGraphqlClient';
import { queryRutilusStatic } from '../../../src/api/graphql/queryRutilusStatic';
import { AppLogic } from '../../../src/components/AppLogic/AppLogic';
import { GlobalLayout } from '../../../src/components/Layouts/GlobalLayout';
import { NOT_FOUND_WITH_REVALIDATION, REVALIDATE } from '../../../src/constants/next';
import { reportMykissError } from '../../../src/helpers/errorHandling';
import { redirectNonExistingFishingWater } from '../../../src/helpers/redirects/redirectNonExistingFishingWater';
import { redirectToFishingWaterWithSlug } from '../../../src/helpers/redirects/redirectToFishingWaterWithSlug';
import { safeSlugify } from '../../../src/helpers/urls';
import {
  GetFishingWater,
  GetFishingWaterNearbyWaters,
  GetFishingWaterNearbyWatersVariables,
  GetFishingWaterNearestPlace,
  GetFishingWaterNearestPlaceVariables,
  GetFishingWaterTopSpecies,
  GetFishingWaterTopSpeciesVariables,
  GetFishingWaterVariables,
} from '../../../src/interfaces/graphql';
import { IPageMetadata } from '../../../src/interfaces/nextjs';
import {
  getFishingWater,
  getFishingWaterNearbyWaters,
  getFishingWaterNearestPlace,
  getFishingWaterTopSpecies,
} from '../../../src/queries/rutilus/FishingWaters';

import { RECENT_CATCH_LIMIT_DAYS } from '../../../src/constants/constants';
import { getSEOStates } from '../../../src/helpers/states/getSEOStates';
import { isExternalId } from '../../../src/helpers/strings/isExternalId';
import { IFishingWaterProps, FishingWater } from '../../../src/views/FishingWater/FishingWater';

type IParams = {
  fishingWaterId: string;
  slug?: string[];
};

export async function getStaticProps({
  params,
}: GetStaticPropsContext<IParams>): Promise<
  GetStaticPropsResult<IPageMetadata & IFishingWaterProps>
> {
  invariant(params, 'Params not found');

  try {
    const client = getRutilusGraphqlClient({ serverMode: 'static' });

    if (!isExternalId(params.fishingWaterId)) {
      return { notFound: true };
    }

    // Redirect with water slug appended
    if (!params.slug) {
      return redirectToFishingWaterWithSlug(params.fishingWaterId, client);
    }

    const getFishingWaterQuery = queryRutilusStatic<GetFishingWater, GetFishingWaterVariables>(
      client,
      {
        query: getFishingWater,
        variables: {
          externalId: params.fishingWaterId,
          recentCatchLimitDays: RECENT_CATCH_LIMIT_DAYS,
        },
      },
    );

    const getTopSpeciesQuery = queryRutilusStatic<
      GetFishingWaterTopSpecies,
      GetFishingWaterTopSpeciesVariables
    >(client, {
      query: getFishingWaterTopSpecies,
      variables: {
        externalId: params.fishingWaterId,
      },
    });

    const getNearestPlaceQuery = queryRutilusStatic<
      GetFishingWaterNearestPlace,
      GetFishingWaterNearestPlaceVariables
    >(client, {
      query: getFishingWaterNearestPlace,
      variables: {
        externalId: params.fishingWaterId,
      },
    });

    const getNearbyWatersQuery = queryRutilusStatic<
      GetFishingWaterNearbyWaters,
      GetFishingWaterNearbyWatersVariables
    >(client, {
      query: getFishingWaterNearbyWaters,
      variables: {
        externalId: params.fishingWaterId,
        recentCatchLimitDays: RECENT_CATCH_LIMIT_DAYS,
      },
    });

    const [
      getFishingWaterResult,
      getNearestPlaceResult,
      getTopSpeciesResult,
      getNearbyWatersResult,
    ] = await Promise.all([
      getFishingWaterQuery,
      getNearestPlaceQuery,
      getTopSpeciesQuery,
      getNearbyWatersQuery,
    ]);

    if (!getFishingWaterResult.response?.data) {
      return redirectNonExistingFishingWater(params.fishingWaterId, REVALIDATE.FIVE_MINS);
    }

    const { displayName } = getFishingWaterResult.response.data.fishingWater;

    // redirect if the slug does not match the slugified displayName
    if (params.slug.length > 0 && params.slug[0] !== safeSlugify(displayName)) {
      return redirectToFishingWaterWithSlug(params.fishingWaterId, client, displayName);
    }

    const nearestPlace = getNearestPlaceResult.response?.data.fishingWater.nearestPlace ?? null;

    const topSpeciesSummary =
      getTopSpeciesResult.response?.data.fishingWater.topSpeciesSummary ?? null;

    const nearbyWaters = getNearbyWatersResult.response?.data.fishingWater.suggestedWaters ?? [];

    // get SEO States
    const countryId = getFishingWaterResult.response.data.fishingWater.country?.id;
    const states = countryId ? getSEOStates(countryId) : [];

    return {
      props: {
        data: getFishingWaterResult.response.data,
        nearestPlace: nearestPlace,
        states: states,
        suggestedWaters: nearbyWaters,
        topSpeciesSummary: topSpeciesSummary,
        _page: 'fishing_water',
        _pageTitle: 'Fishing Water',
        _pageId: getFishingWaterResult.response.data.fishingWater._id,
        _pageSubject: getFishingWaterResult.response.data.fishingWater.displayName,
      },
      revalidate: REVALIDATE.FOUR_HOURS,
    };
  } catch (error) {
    reportMykissError(error);
    return NOT_FOUND_WITH_REVALIDATION;
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<IParams>> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

FishingWater.getLayout = function getLayout(page: any) {
  return (
    <AppLogic pageProps={page.props}>
      <GlobalLayout
        stickyBannerGetAppDescription={`See all catches in ${page.props.data.fishingWater.displayName}`}
      >
        {page}
      </GlobalLayout>
    </AppLogic>
  );
};

export default FishingWater;
